import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Trophy, ShieldCheck, Flame } from "lucide-react";

const REGIOES = ["Norte", "Nordeste", "Centro-Oeste", "Sudeste", "Sul"];

const PartnerRanking = () => {
  const [rows, setRows] = useState([]);
  const [consultores, setConsultores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("");
  const [uf, setUf] = useState("");
  const [region, setRegion] = useState("");

  const reload = React.useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (city) qs.set("city", city);
    if (uf) qs.set("state", uf);
    if (region && !uf) qs.set("region", region);
    const clinicsQ = qs.toString();
    const consQs = new URLSearchParams();
    if (uf) consQs.set("state", uf);
    if (region && !uf) consQs.set("region", region);
    Promise.all([
      api.get(`/public/ranking?${clinicsQ}`),
      api.get(`/public/ranking/consultores?${consQs.toString()}`).catch(() => ({ data: [] })),
    ]).then(([a, b]) => { setRows(a.data); setConsultores(b.data); })
      .finally(() => setLoading(false));
  }, [city, uf, region]);

  useEffect(() => {
    reload();
    const t = setInterval(reload, 60000); // auto-refresh 60s
    return () => clearInterval(t);
  }, [reload]);

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Transparência</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] mt-1 flex items-center gap-3">
          <Trophy className="w-8 h-8 text-amber-500" /> Ranking Unidades Parceiras
        </h1>
        <p className="text-slate-600 mt-2 text-sm max-w-2xl">
          Indicadores anônimos de performance operacional da rede. Nomes das unidades são anonimizados nesta visão pública — as métricas mostram o compromisso da rede como um todo.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm" data-testid="ranking-filters">
          <input
            data-testid="ranking-filter-city"
            placeholder="Filtrar por cidade"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm"
          />
          <select
            data-testid="ranking-filter-uf"
            value={uf}
            onChange={(e) => { setUf(e.target.value); if (e.target.value) setRegion(""); }}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm"
          >
            <option value="">UF (todos)</option>
            {["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"].map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
          <select
            data-testid="ranking-filter-region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            disabled={!!uf}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            title={uf ? "UF selecionada — remova a UF para filtrar por região" : ""}
          >
            <option value="">Região (todas)</option>
            {REGIOES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {(city || uf || region) && (
            <button
              onClick={() => { setCity(""); setUf(""); setRegion(""); }}
              data-testid="ranking-filter-clear"
              className="text-xs text-slate-500 hover:text-slate-800 underline"
            >Limpar</button>
          )}
        </div>
      </div>

      {loading ? <div className="text-slate-500">Carregando…</div> : rows.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl">
          <p className="text-slate-500">Ranking ainda não disponível — precisamos de mais reservas para calcular.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="ranking-table">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr className="whitespace-nowrap">
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Unidade</th>
                <th className="px-4 py-3 text-left">Cidade</th>
                <th className="px-4 py-3 text-right">Reservas</th>
                <th className="px-4 py-3 text-right">Taxa resposta</th>
                <th className="px-4 py-3 text-right">Taxa aceite</th>
                <th className="px-4 py-3 text-right">Resp. média</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.rank} data-testid={`rank-row-${r.rank}`} className="hover:bg-slate-50 whitespace-nowrap">
                  <td className="px-4 py-2.5 font-bold text-[#0B2B62]">{r.rank}º</td>
                  <td className="px-4 py-2.5 font-semibold text-slate-800">{r.code}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{r.city}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{r.total_reservations}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-green-700">{(r.response_rate * 100).toFixed(0)}%</td>
                  <td className="px-4 py-2.5 text-right font-mono text-blue-700">{(r.acceptance_rate * 100).toFixed(0)}%</td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-600">{r.avg_response_hours != null ? `${r.avg_response_hours}h` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <p className="text-[11px] text-slate-400 mt-4 flex items-center gap-1.5">
        <ShieldCheck className="w-3 h-3" /> Métricas atualizadas dinamicamente com base nas reservas confirmadas na plataforma.
      </p>

      {/* Streak Ranking Consultores */}
      <div className="mt-12">
        <div className="flex items-center gap-3 mb-4">
          <Flame className="w-7 h-7 text-orange-500" />
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Consistência</div>
            <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0B2B62]">Top-10 Consultores por Streak</h2>
          </div>
        </div>
        <p className="text-slate-600 mt-1 text-sm max-w-2xl mb-4">
          Dias consecutivos com atividade na plataforma. Nomes anonimizados. Recompensa a rotina que salva vidas — quem aparece todo dia.
        </p>
        {consultores.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-slate-200 rounded-xl text-slate-500 text-sm">
            Nenhum consultor ativo ainda — o ranking aparece quando a rede começa a rodar.
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="streak-ranking-table">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                <tr className="whitespace-nowrap">
                  <th className="px-4 py-3 text-left">#</th>
                  <th className="px-4 py-3 text-left">Consultor</th>
                  <th className="px-4 py-3 text-right">Streak (dias)</th>
                  <th className="px-4 py-3 text-right">Ativo em 60d</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {consultores.map((c) => (
                  <tr key={c.rank} data-testid={`streak-row-${c.rank}`} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5 font-bold text-[#0B2B62]">{c.rank}º</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {c.avatar_url ? (
                          <img src={c.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover border border-slate-200" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 text-xs font-semibold">
                            {(c.code || "?").slice(0,1)}
                          </div>
                        )}
                        {c.id ? (
                          <Link to={`/consultores/${c.id}`} className="font-semibold text-slate-800 hover:text-[#0B2B62] hover:underline">{c.code}</Link>
                        ) : (
                          <span className="font-semibold text-slate-800">{c.code}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 font-bold">
                        🔥 {c.streak_days}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-600">{c.active_days_60d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerRanking;
